'use client'
import { FC, useEffect, useRef, useState, Fragment } from 'react';
import { Content } from "@prismicio/client";
import { SliceComponentProps, PrismicRichText } from '@prismicio/react'
import { PrismicNextImage } from '@prismicio/next'
import Slider from 'react-slick'
import { Campaign, VideoPlayer } from '@/components'
import Link from 'next/link'



/**
 * Props for `AddressChecker`.
 */
export type AddressCheckerProps =
  SliceComponentProps<Content.AddressCheckerSlice>;

/**
 * Component for "AddressChecker" Slices.
 */
const AddressChecker: FC<AddressCheckerProps> = ({ slice }) => {

  const formatYYYYMMDD = (date: Date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return Number(`${y}${m}${d}`);
  };

  const formatMMMYYYY = (date: Date) =>
    new Intl.DateTimeFormat('en-NZ', { month: 'short', year: 'numeric' }).format(date);

  const slickRef = useRef(null) as any;
  const resultRef = useRef(null) as any;
  const inputArea = useRef(null) as any;
  const [videoOverlay, setVideoOverlay] = useState(false) as any;
  const [videoURL, setVideoURL]=useState("")
  const [videoImage, setVideoImage]=useState("")
  const [, setPlayStatus] = useState(false)
  const [searching, setSearching] = useState(false);
  const [addressList, setAddressList] = useState(null) as any
  const [white, setWhite] = useState("")
  const todayDate = formatYYYYMMDD(new Date());
  const twoYears = new Date();
  twoYears.setFullYear(twoYears.getFullYear() + 2);
  const twoYearsDate = formatYYYYMMDD(twoYears);
  const [targetDate, setTargetDate] = useState("");
  const [tokenValid, setTokenValid] = useState(false) as any
  const [placeIdData, setPlaceId] = useState(false);
  const [addressData, setAddress] = useState(false);
  const [, setReadyEntry] = useState(false);

  const readToken = () => {
    if (typeof window === "undefined") return null;
    try {
      const raw = window.localStorage.getItem("tfa");
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (parsed?.expire && Date.now() <= Number(parsed.expire) && parsed?.response) {
        return parsed.response;
      }
    } catch {
      // fallback
    }
    return null;
  };

  const writeToken = (response: any) => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem("tfa", JSON.stringify({
        expire: Date.now() + 3600 * 1000,
        response,
      }));
    } catch {
      // fallback
    }
  };
  
  async function getToken() {
    try {
      const res = await fetch("/api/address/auth");
      const response = await res.json();
      if (response && typeof response === "string") {
        setTokenValid(true); 
        setReadyEntry(true);
        writeToken(response);
        return response;
      }
    } catch (error) {
      console.error('Error fetching token:', error);
    }
    return null;
  }

  const resolveTokenResponse = async () => {
    const existing = readToken();
    if (existing) {
      setTokenValid(true);
      setReadyEntry(true);
      return existing;
    }
    return await getToken();
  };

  useEffect(()=>{
  
    async function serialLookup(serialNumber: string){
      const tokenResponse = await resolveTokenResponse();
      if(tokenResponse)  {
        setAddressList(null);
        const addressSearch = await fetch("/api/address", {method:"post", body: JSON.stringify({address: serialNumber, token: tokenResponse})});
        const addressSearchRes = await addressSearch.json();
        if(addressSearchRes?.hits?.[0]) {
          await getProducts({display: addressSearchRes.hits[0].displayAddress, placeId: addressSearchRes.hits[0].placeId, legacy: "directlink", index: 1});
          setReadyEntry(true);
        }
      }
      return null;
    }

    const paramsString = new URL(window.location.href);
    const searchParams = new URLSearchParams(paramsString.search);
    const placeId = searchParams.get('placeId');
    const address = searchParams.get('address');
    const serialNumber = searchParams.get('sn') || searchParams.get('SN');
    const unisontui = searchParams.get('tui');

    resolveTokenResponse().then(token => {
      if (token) {
        setTokenValid(true);
        setReadyEntry(true);
      }
    });

    if(serialNumber) {
      if (resultRef?.current) resultRef.current.classList.add("hide");
      setReadyEntry(false);
      serialLookup(serialNumber);
    } else if(address) {
      if(placeId === "SN") {
        if (resultRef?.current) resultRef.current.classList.add("hide");
        setReadyEntry(false);
        serialLookup(address);
      }
      else {
        if (resultRef?.current) resultRef.current.classList.add("hide");
        getProducts({display: address, placeId: placeId, legacy: "directlink", tui: unisontui, index: 1});
      }
    }
  }, []);
  
  const getAddress = async (e: any) =>{
    const address = e.target?.value;
    if (!address) {
      setAddressList(null);
      return;
    }

    const tokenResponse = await resolveTokenResponse();
    if (!tokenResponse) {
      return;
    }

    if (resultRef?.current) resultRef.current.classList.remove("hide"); 
    await fetch("/api/address", {method:"post", body: JSON.stringify({address: address, token: tokenResponse})}).then(res => res.json())
      .then(response => {setAddressList(response);  
        // console.log(response)
      })
      .catch(error => console.error('Error:', error));
  }
  
  const getProducts = async (dataSet: any) => {
    setAddress(dataSet.display)
    setPlaceId(dataSet.placeId)
    setAddressList(null)
    setSearching(true);
    const tokenResponse = await resolveTokenResponse()
    if(!tokenResponse) {
      setSearching(false)
      return
    }

    const paramsString = new URL(window.location.href);
    const searchParams = new URLSearchParams(paramsString.search);
    const placeId = searchParams.get('placeId');
    if (inputArea.current) {
      inputArea.current.value = dataSet.display || ""; 
    }

    if(!placeId || placeId === "undefined") {
      if (resultRef?.current) resultRef.current.classList.add("hide");
    }

    await fetch("/api/address/products", {method:"post", body: JSON.stringify({placeId: dataSet.placeId, token: tokenResponse, tui: dataSet.tui})}).then(res => res.json())
    .then(response => { 

      setWhite('white');

      const placeId = response.error !== "placeID" ? true : false;
      const fdhState = response.fdhState;
      const placeServiceability = response.placeServiceability === "Serviceable" ? true : false;
      const availableProducts = response.availableProducts;
      const fdhAvailableDate = formatYYYYMMDD(new Date(response.fdhAvailableDate));
      const inventory = response.inventory;
      let preinstall = false as any
      let buildStatus = false as any
      let active = false as any
      let statusSlide = 13
      let managed = false as any

      // Check for product available should be limited to BITSTREAM2 check column on sheet

      if(placeId === true) {
        
        availableProducts?.map((item: any)=>{
          // console.log(item)
          if(item.productOfferCode === "PRE-INSTALL") {
            preinstall = true
            buildStatus = item.buildStatus;
          }
          if(item.productOfferName === "Bitstream 2 Ultra 1G/500") {
            buildStatus = item.buildStatus;
          }
          item.additionalReq?.map((req: any)=>{
            if(req === "Managed") {
              managed = true
            }
            return null
          })
          return null
        })

        inventory?.map((item: any)=>{
          active = item.status === "Active" ? true : false
        })

        fdhAvailableDate > todayDate && fdhAvailableDate < twoYearsDate ? setTargetDate(formatMMMYYYY(new Date(response.fdhAvailableDate))) : setTargetDate("")

        switch(fdhState) {
          case 'Approved':
            statusSlide = preinstall === false ? 8 : managed === true ? 11 : 6;
          break;
          case 'Under Construction':
            statusSlide = preinstall === false ? 9 : managed === true ? 12 : 4;
          break;
          case 'In Service':
            if(placeServiceability === true) {
              if(active === true) {
                statusSlide = 1
              }
              else {
                if(buildStatus === "intact") {
                  statusSlide = 2
                }
                if(buildStatus === "required" && preinstall === false) {
                  statusSlide = 7
                }
                if(buildStatus === "required" && preinstall === true) {
                  statusSlide = 3
                }
                if(buildStatus === "required" && preinstall === true && managed === true) {
                  statusSlide = 10
                }
              }
              
            }
            if(placeServiceability === false) {
              statusSlide = 5
            }
          break;
          default:
            
        }
      }

      placeId !== true ? statusSlide = 14 : null

      // Change the slide
      
      slickRef.current?.slickGoTo(statusSlide)

      setSearching(false)

    })
    .catch(error => console.error('Error:', error));
  
}

  const clearAddress = () =>{
    if (inputArea.current) inputArea.current.value = ""; 
    if (resultRef?.current) resultRef.current.classList.remove("hide"); 
    if (slickRef?.current) slickRef.current.slickGoTo(0);
  }

  const settings = {
    dots: false,
    arrows: false,
    infinite: false,
    slidesToShow: 1,
    adaptiveHeight: true,
    fade: false,
    swipe: false,
    swipeToSlide: false,
    touchMove: false, 
    draggable: false
  };

  function showVideo(videoURL: any, videoStill:any ) {
    setVideoOverlay(true)
    setVideoURL(videoURL)
    setVideoImage(videoStill)
    setPlayStatus(true)
  } 

  return (
    <section 
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="w-screen font-fellix mb-12"
    >
    {(videoOverlay && videoURL !== null) && (<VideoPlayer dataSet={{video_link: {url: videoURL}, image: {url: videoImage}, play: true}}/> )}
    <div className={slice.primary.mini_version === true ? "address-checker mini "+ white : "address-checker"}>
        <div className="absolute z-[5] w-full">
          <div className="max-w-[550px] mx-auto">
          <article className={`${slice.primary.mini_version === true ? "text-jade" : "text-white"} mt-24 text-[48px] leading-[60px] font-normal px-4 text-center mb-[30px] w-full max-w-none`}>
            <PrismicRichText field={slice.primary.title}/>
          </article>
          <article className={`${slice.primary.mini_version === true ? "text-jade" : "text-white"} text-[28px] leading-[36px] font-normal text-center mx-auto mb-[52px] w-full px-4`}>
            <PrismicRichText field={slice.primary.intro}/>
          </article>
          <div className="absolute px-4 top-[320px] left-0 w-full">
            <input className="w-full max-w-[700px] mx-auto relative z-10 rounded-full p-4 bg-lightgrey drop-shadow-lg block mt-6 text-center text-[19px] leading-[19px] placeholder:text-jade outline-none appearance-none" type="text" placeholder={tokenValid === true ? "Enter your address" : "Loading..."} onChange={(e: any)=>getAddress(e)} ref={inputArea}/>
          {searching === true ?
            <div className="flex items-center justify-center h-full absolute right-3 top-0 z-10">
              <div className="animate-spin rounded-full h-8 w-8 border-4 border-t-orange border-gray-200"></div>
            </div>
            : null }
          </div>
          {addressList?.hits?.length > 0 ? 
            <div className="z-9 absolute top-[380px] left-0 right-0" ref={resultRef}>
              <div className="pb-4 bg-white border-jade/10 border-[1px] rounded-b-[20px] drop-shadow pt-8 w-[90dvw] max-w-[700px] mx-auto" >
                <ul className="text-[14px] leading-[16px] w-full block">
                  {addressList?.hits?.map((item: any, index: number)=>{
                    return (
                      <Fragment  key={index} >
                      {item.placeId ? 
                      <li className="w-full p-4 border-b-[1px] border-b-jade/10 cursor-pointer  hover:text-orange" onClick={()=>{window.location.href="/address-checker?address="+item.displayAddress+"&placeId="+item.placeId}}>{item.displayAddress}</li>
                      : <li className="w-full p-4 border-b-[1px] border-b-jade/10 cursor-pointer  hover:text-orange" onClick={()=>{window.location.href="/address-checker?address="+item.displayAddress}}>{item.displayAddress}</li>}
                      </Fragment>
                    )
                  })}
                </ul>
              </div>
            </div>
          : null}
          {addressList?.hits && addressList.hits.length < 1 && inputArea.current?.value ?
          <div className="z-9 absolute top-[380px] left-0 right-0" ref={resultRef}>
            <div className="pb-4 bg-white border-jade/10 border-[1px] rounded-b-[20px] z-9 pt-12 drop-shadow px-4 w-[90dvw] max-w-[700px] mx-auto" ref={resultRef}>
              <ul >
                <li>Sorry that address isn&apos;t in our service area.<br/>Doesn&apos;t look right? Click <a href={"/form-address-details-check"} style={{color: 'blue'}}>here</a></li>
              </ul>
            </div>
            </div>
          : null}
          {searching !== true ?
            <p className="clear-address" onClick={()=>clearAddress()}></p>
          : null}
          </div>
        </div>
        <div className="">
          <Slider {...settings} ref={slickRef}>
            {slice.primary.responses?.map((item: any, index: number)=>{
              return (
                <div key={index}>
                  <div className="">
                      {item.image ? 
                      <PrismicNextImage className="h-[376px] mb-12 w-full object-cover" field={item.image} fallbackAlt=""/>
                      : <></>}
                      <div className="max-w-[1280px] mx-auto text-center">
                      {item.icon.url ? <p className="look-right">Doesn&apos;t look right? Click <a href={"/form-address-details-check?place_id="+placeIdData+"&address="+addressData} style={{color: 'blue'}}>here.</a></p>: null}
                      {item.icon.url ?
                      <>
                      <PrismicNextImage className="w-24 h-24 mx-auto block my-8" field={item.icon} fallbackAlt=""/>
                      <article className="text-[28px] leading-[36px] font-semibold">
                        <PrismicRichText field={item.status}/>
                      </article>
                      <p dangerouslySetInnerHTML={{__html: targetDate}} />
                      <p className="text-orange mt-8 mb-8">_________</p>
                      <article className="text-[28px] leading-[36px] mx-auto max-w-[550px] pb-16 prose prose-a:underline prose-a:font-normal">
                      <PrismicRichText field={item.description} />
                      </article>
                      {item.link && item.link.uid ? 
                      <Link href={item.link.uid} className="button mb-16 text-[16px]">{item.link_text}</Link>
                      : null }
                      {item.second_link && item.second_link.uid ? 
                        <div className="extra-panel">
                          <div className="target-text">
                            <PrismicRichText field={item.second_title}/>
                            <PrismicRichText field={item.second_description}/>
                          </div>
                          <Link href={item.second_link.uid} className="button mb-16 text-[16px]">{item.second_link_text}
                          </Link>
                        </div>  
                      : null }
                      {item.show_form ===true ?
                        <form className="form grey">
                          <input type="text" name="name" placeholder="Please tell us your name"/>
                          <p><strong>Pick your preferred communication</strong></p>
                          <p>By email</p>
                          <input type="email" name="name" placeholder="Enter your email address"/>
                          <input type="submit" name="name" value="submit"/>
                        </form>
                      : null}
                    </>
                    : null }
                  </div>
                    {item.info_panel_one_title.length > 0 ? 
                      <div className="bg-lightgrey py-16 mt-16">
                          <div className="grid grid-cols-12 gap-0 md:gap-16 max-w-[800px] mx-auto">
                            <div className="col-span-12 md:col-span-6">
                              <div className="bg-white p-8 h-full text-center relative pb-32">
                                <div className="">
                                  <PrismicNextImage field={item.info_panel_one_image} className="mx-auto my-12"fallbackAlt="" />
                                </div>
                                <article className="text-[28px] leading-[36px] font-semibold text-center mb-4">
                                <PrismicRichText field={item.info_panel_one_title}/>
                                </article>
                                <article className="text-center text-[18px] leading-[26px] prose prose-a:underline">
                                <PrismicRichText field={item.info_panel_one_paragraph}/>
                                </article>
                                <a className="button my-8 absolute bottom-0 left-[50%] -translate-x-[50%]" 
                                        onClick={()=>{
                                        if(item.info_panel_one_link.url) {
                                          window.location.href = item.info_panel_one_link.url
                                        }
                                        if(item.info_panel_one_link.uid) {
                                          window.location.href = item.info_panel_one_link.uid
                                        }
                                        if(item.video_link_one.url) {
                                          showVideo(item.video_link_one.url, item.preview_video_one_image?.url)
                                        }
                                      }}
                                >{item.info_panel_one_link_text}
                                </a>
                              </div>
                            </div>
                            <div className="col-span-12 md:col-span-6 panel">
                              <div className="bg-white p-8 h-full text-center relative pb-32">
                                <div className="icon-wrapper">
                                  <PrismicNextImage field={item.info_panel_two_image} className="mx-auto my-12" fallbackAlt="" />
                                </div>
                                <article className="text-[28px] leading-[36px] font-semibold text-center mb-4">
                                  <PrismicRichText field={item.info_panel_two_title}/>
                                </article>
                                <article className="text-center text-[18px] leading-[26px]">
                                  <PrismicRichText field={item.info_panel_two_paragragh}/>
                                </article>
                                <a className="button my-8 absolute bottom-0 left-[50%] -translate-x-[50%]"  
                                        onClick={()=>{
                                        if(item.info_panel_two_link.url) {
                                          window.location.href=item.info_panel_two_link.url
                                        }
                                        if(item.info_panel_two_link.uid) {
                                          window.location.href=item.info_panel_two_link.uid
                                        }
                                        if(item.video_link_two.url) {
                                          showVideo(item.video_link_two.url, item.preview_video_two_image?.url)
                                        }
                                      }}
                                >{item.info_panel_two_link_text}
                                </a>
                              </div>
                            </div>
                          </div>
                        </div>
                      : null }
                    {item.campaign_link?.id ? <Campaign linkId={item.campaign_link.id}/>  : <></> }
                    <div className="address-padding"></div>
                  </div>
                </div>
              )
            })}
            </Slider>
        </div>
      </div>
    </section>
  );
};

export default AddressChecker;
